import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { getAnnotationColor } from "../../annotationColors";

// Standard amino acid HET codes — excluded because they are protein residues,
// not ligands. Modified residues (e.g. TPO, SEP, PTR) are kept.
const STANDARD_AMINO_ACIDS = new Set([
  "ALA", "ARG", "ASN", "ASP", "CYS",
  "GLN", "GLU", "GLY", "HIS", "ILE",
  "LEU", "LYS", "MET", "PHE", "PRO",
  "SER", "THR", "TRP", "TYR", "VAL",
]);

// Other non-ligand HET codes commonly found in PDB structures.
// Extend as needed.
const OTHER_EXCLUDED = new Set([
  "HOH",          // water
  // "MSE",       // selenomethionine — uncomment to exclude
]);

const isExcluded = (het) =>
  STANDARD_AMINO_ACIDS.has(het) || OTHER_EXCLUDED.has(het);

const LigandAnnotationDonut = ({ ligandData }) => {
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});

  useEffect(() => {
    if (!Array.isArray(ligandData) || ligandData.length === 0) {
      setPlotData([]);
      return;
    }

    // Count UNIQUE ligands per annotation (not raw rows — same ligand can
    // appear in many rows because it contacts many residues)
    const seen = new Set();
    const counts = {};

    ligandData.forEach((row) => {
      const het = (row?.ligand || "").trim().toUpperCase();

      // Skip standard amino acids and other non-ligand residues
      if (isExcluded(het)) return;

      const annotation = (row?.annotation || "unknown").trim();
      const key = `${annotation}|${row?.inchikey || row?.ligand || ""}|${row?.pdbId || ""}`;
      if (seen.has(key)) return;
      seen.add(key);

      counts[annotation] = (counts[annotation] || 0) + 1;
    });

    // Sort categories by count (descending)
    const sortedEntries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const labels = sortedEntries.map(([k]) => k);
    const values = sortedEntries.map(([, v]) => v);
    const colors = labels.map((label) => getAnnotationColor(label));

    setPlotData([
      {
        labels,
        values,
        type: "pie",
        hole: 0.55,
        textinfo: "label+percent",
        textposition: "auto",
        automargin: true,
        insidetextorientation: "radial",
        hovertemplate: "%{label}<br>Unique ligands: %{value}<br>%{percent}<extra></extra>",
        marker: {
          colors,
          line: { color: "#fff", width: 2 },
        },
        sort: false,
      },
    ]);

    setLayout({
      autosize: true,
      font: { size: 9 },
      title: { text: "Ligand Annotation Distribution" },
      showlegend: true,
      legend: {
        orientation: "v",
        x: 1.02,
        y: 0.5,
      },
      margin: { t: 50, b: 40, l: 20, r: 80 },
      annotations: [
        {
          text: `${values.reduce((a, b) => a + b, 0)}<br>ligands`,
          showarrow: false,
          font: { size: 14, color: "#333" },
          x: 0.5,
          y: 0.5,
        },
      ],
    });
  }, [ligandData]);

  return (
    <div className="aspect-square card">
      <Plot
        data={plotData}
        layout={layout}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default LigandAnnotationDonut;